import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from '../../api/axios';
import Banner from '../../ui/Banner';
import Pagination from '../../ui/Pagination';
import PolicyCustomSelect from './PolicyCustomSelect';
import './PolicyCheckpoint.css';

const DEFAULT_RESOLUTION_ACTIONS = [
  {
    id: 'false_positive',
    label: 'False Positive (Benign Input)',
    badgeClass: 'info',
    description:
      'The query was legitimate business activity incorrectly matched by an aggressive pattern.',
  },
  {
    id: 'user_warned',
    label: 'User Warned & Compliance Explained',
    badgeClass: 'warning',
    description:
      'The user was contacted and informed of platform acceptable use policy and prohibited patterns.',
  },
  {
    id: 'attack_neutralized',
    label: 'Malicious Attempt Neutralized',
    badgeClass: 'success',
    description:
      'Adversarial jailbreak, injection, or exploit attempt was verified blocked and incident safely closed.',
  },
  {
    id: 'credentials_rotated',
    label: 'Exposed Secret / Key Rotated',
    badgeClass: 'danger',
    description:
      'Leaked API token, private key, or password was confirmed revoked and rotated in external provider.',
  },
  {
    id: 'policy_exception',
    label: 'Policy Exception Approved',
    badgeClass: 'purple',
    description:
      'Authorized security exception granted for this workflow after formal compliance review.',
  },
  {
    id: 'mitigated_other',
    label: 'Reviewed & Mitigated (Other)',
    badgeClass: 'neutral',
    description:
      'Incident investigation complete, corrective action taken, and record formally archived.',
  },
];

const CATEGORY_DESCRIPTIONS = {
  credentials_secrets: 'API keys, tokens, private keys, database URIs, passwords',
  prompt_injection_jailbreak: 'Instruction overrides, DAN mode, jailbreak personas, prompt leakage',
  pii_financial: 'Credit cards, bank accounts, NRIC/SSN, sensitive personal data',
  prohibited_terms: 'Malware payloads, remote shell commands, data exfiltration scripts',
  custom: 'Workspace-specific security rules and proprietary keywords',
};

const PATTERN_TYPE_DESCRIPTIONS = {
  keyword: 'Matches exact whole words or phrases (case-insensitive)',
  regex: 'Regular expression with wildcards and token lengths',
  contains: 'Matches substring anywhere within words or prompt text',
};

const SEVERITY_DESCRIPTIONS = {
  critical: 'Highest threat level: immediate hard block and alerts',
  high: 'High severity: blocks execution and requires review',
  medium: 'Moderate risk: flagged and logged for periodic audit review',
  low: 'Informational advisory: recorded in threat telemetry logs',
};

const ACTION_DESCRIPTIONS = {
  block: 'Rejects prompt before reaching LLM, returns 403 Forbidden',
  alert: 'Allows execution while recording incident for review',
};

export default function PolicyCheckpoint({ currentUser }) {
  // Navigation tabs: 'incidents' | 'rules' | 'audits'
  const [activeTab, setActiveTab] = useState('incidents');
  const [loading, setLoading] = useState(true);

  // Pagination State for each tab
  const [incidentsPage, setIncidentsPage] = useState(1);
  const [incidentsPerPage, setIncidentsPerPage] = useState(10);

  const [rulesPage, setRulesPage] = useState(1);
  const [rulesPerPage, setRulesPerPage] = useState(10);

  const [auditsPage, setAuditsPage] = useState(1);
  const [auditsPerPage, setAuditsPerPage] = useState(10);

  // Dynamic system configuration (Zero hardcoding)
  const [policyConfig, setPolicyConfig] = useState({
    categories: [],
    severities: [],
    actions: [],
    patternTypes: [],
    services: [],
    resolutionActions: DEFAULT_RESOLUTION_ACTIONS,
  });

  // Live data records
  const [stats, setStats] = useState(null);
  const [violations, setViolations] = useState([]);
  const [rules, setRules] = useState([]);
  const [auditReports, setAuditReports] = useState([]);

  // Search & Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [serviceFilter, setServiceFilter] = useState('');
  const [severityFilter, setSeverityFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');

  // Modals & Action loading
  const [showAddRuleModal, setShowAddRuleModal] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Incident Investigation & Resolution Modal States
  const [resolvingIncident, setResolvingIncident] = useState(null);
  const [viewingIncident, setViewingIncident] = useState(null);
  const [modalError, setModalError] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null);
  const [resolutionForm, setResolutionForm] = useState({
    resolutionAction: 'false_positive',
    notes: '',
  });

  // Handle ESC key to dismiss confirmation modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && confirmModal && !actionLoading) {
        setConfirmModal(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [confirmModal, actionLoading]);

  const closeConfirmModal = () => {
    if (!actionLoading) {
      setConfirmModal(null);
    }
  };

  const handleDialogConfirm = async () => {
    if (confirmModal?.onConfirm) {
      await confirmModal.onConfirm();
    }
  };

  // Track expanded multi-rule pills for cleaner table layout
  const [expandedRulesMap, setExpandedRulesMap] = useState({});

  const toggleExpandRules = (violationId) => {
    setExpandedRulesMap((prev) => ({
      ...prev,
      [violationId]: !prev[violationId],
    }));
  };

  // New Rule Form (initialized dynamically once config loads)
  const [newRule, setNewRule] = useState({
    name: '',
    pattern: '',
    pattern_type: 'keyword',
    category: 'credentials_secrets',
    severity: 'high',
    action: 'block',
    description: '',
  });

  const [notification, setNotification] = useState(null);

  const showNotice = (msg, type = 'success') => {
    setNotification({ msg, type });
    setTimeout(() => setNotification(null), 4000);
  };

  // 1. Fetch Dynamic Policy Configuration (Categories, Severities, Actions, Monitored Services, Resolutions)
  const fetchConfig = useCallback(async () => {
    try {
      const res = await axios.get('/admin/policy/config');
      if (res.data?.success && res.data.data) {
        const d = res.data.data;
        const normalized = {
          categories: Array.isArray(d.categories)
            ? d.categories
            : Array.isArray(d.POLICY_CATEGORIES)
              ? d.POLICY_CATEGORIES
              : [],
          severities: Array.isArray(d.severities)
            ? d.severities
            : Array.isArray(d.POLICY_SEVERITIES)
              ? d.POLICY_SEVERITIES
              : [],
          actions: Array.isArray(d.actions)
            ? d.actions
            : Array.isArray(d.POLICY_ACTIONS)
              ? d.POLICY_ACTIONS
              : [],
          patternTypes: Array.isArray(d.patternTypes)
            ? d.patternTypes
            : Array.isArray(d.PATTERN_TYPES)
              ? d.PATTERN_TYPES
              : [],
          services: Array.isArray(d.services)
            ? d.services
            : Array.isArray(d.MONITORED_SERVICES)
              ? d.MONITORED_SERVICES
              : [],
          resolutionActions:
            Array.isArray(d.resolutionActions) && d.resolutionActions.length > 0
              ? d.resolutionActions
              : Array.isArray(d.RESOLUTION_ACTIONS) && d.RESOLUTION_ACTIONS.length > 0
                ? d.RESOLUTION_ACTIONS
                : DEFAULT_RESOLUTION_ACTIONS,
        };
        setPolicyConfig(normalized);
        // Default newRule fields to first available configured option
        if (normalized.patternTypes?.[0]?.id) {
          setNewRule((prev) => ({
            ...prev,
            pattern_type: normalized.patternTypes[0].id,
            category: normalized.categories?.[0]?.id || 'custom',
            severity: normalized.severities?.[0]?.id || 'high',
            action: normalized.actions?.[0]?.id || 'block',
          }));
        }
      }
    } catch (err) {
      console.warn('[PolicyCheckpoint] Failed to fetch dynamic config:', err.message);
    }
  }, []);

  // 2. Fetch KPI Stats
  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get('/admin/policy/stats');
      if (res.data?.success) {
        setStats(res.data.data);
      }
    } catch (err) {
      console.warn('[PolicyCheckpoint] Failed to fetch stats:', err.message);
    }
  }, []);

  // 3. Fetch Violations
  const fetchViolations = useCallback(async () => {
    try {
      const res = await axios.get('/admin/policy/violations');
      if (res.data?.success) {
        const list = res.data.data?.violations || res.data.data;
        setViolations(Array.isArray(list) ? list : []);
      }
    } catch (err) {
      console.warn('[PolicyCheckpoint] Failed to fetch violations:', err.message);
    }
  }, []);

  // 4. Fetch Rules
  const fetchRules = useCallback(async () => {
    try {
      const res = await axios.get('/admin/policy/rules');
      if (res.data?.success) {
        const list = res.data.data?.rules || res.data.data;
        setRules(Array.isArray(list) ? list : []);
      }
    } catch (err) {
      console.warn('[PolicyCheckpoint] Failed to fetch rules:', err.message);
    }
  }, []);

  // 5. Fetch Audit Reports
  const fetchAuditReports = useCallback(async () => {
    try {
      const res = await axios.get('/admin/policy/audit-reports');
      if (res.data?.success) {
        const list = res.data.data?.reports || res.data.data;
        setAuditReports(Array.isArray(list) ? list : []);
      }
    } catch (err) {
      console.warn('[PolicyCheckpoint] Failed to fetch audit reports:', err.message);
    }
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([
      fetchConfig(),
      fetchStats(),
      fetchViolations(),
      fetchRules(),
      fetchAuditReports(),
    ]);
    setLoading(false);
  }, [fetchConfig, fetchStats, fetchViolations, fetchRules, fetchAuditReports]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // Handle Add Rule Form Submit
  const handleCreateRule = async (e) => {
    e.preventDefault();
    if (!newRule.name.trim() || !newRule.pattern.trim()) {
      showNotice('Rule name and pattern are required', 'error');
      return;
    }

    try {
      setActionLoading(true);
      const res = await axios.post('/admin/policy/rules', newRule);
      if (res.data?.success) {
        showNotice('Sensitive keyword rule created and compiled successfully');
        setShowAddRuleModal(false);
        setNewRule((prev) => ({
          ...prev,
          name: '',
          pattern: '',
          description: '',
        }));
        await fetchRules();
        await fetchStats();
      }
    } catch (err) {
      showNotice(err.response?.data?.error || 'Failed to create rule', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  // Toggle Rule Status (predefined system rules cannot be disabled)
  const handleToggleRule = async (rule) => {
    if (rule.is_system) {
      showNotice(
        `Predefined system policy "${rule.name}" is a mandatory platform guardrail and cannot be disabled.`,
        'error'
      );
      return;
    }
    try {
      const res = await axios.put(`/admin/policy/rules/${rule.id}`, {
        is_active: !rule.is_active,
      });
      if (res.data?.success) {
        showNotice(`Rule "${rule.name}" is now ${!rule.is_active ? 'Active' : 'Disabled'}`);
        await fetchRules();
        await fetchStats();
      }
    } catch (err) {
      showNotice(err.response?.data?.error || 'Failed to update rule status', 'error');
    }
  };

  // Delete Rule (blocks deletion of predefined system rules)
  const executeDeleteRule = async (id) => {
    try {
      setActionLoading(true);
      const res = await axios.delete(`/admin/policy/rules/${id}`);
      if (res.data?.success) {
        showNotice('Rule deleted successfully');
        setConfirmModal(null);
        await fetchRules();
        await fetchStats();
      } else {
        showNotice(res.data?.error || 'Failed to delete rule', 'error');
      }
    } catch (err) {
      showNotice(err.response?.data?.error || 'Failed to delete rule', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteRule = (id, name, isSystem = false) => {
    if (isSystem) {
      showNotice('Predefined system security policies cannot be deleted.', 'error');
      return;
    }
    setConfirmModal({
      title: 'Delete Policy Rule?',
      message: `Are you sure you want to remove policy rule "${name}"?`,
      detail:
        'This rule will be permanently deleted and will no longer intercept incoming prompts or AI completions.',
      confirmText: 'Delete Rule',
      cancelText: 'Cancel',
      variant: 'danger',
      icon: 'fa-trash-alt',
      onConfirm: () => executeDeleteRule(id),
    });
  };

  // Available SOC Investigation Dispositions (dynamic from API or standard defaults)
  const availableResolutionActions = useMemo(() => {
    return Array.isArray(policyConfig.resolutionActions) &&
      policyConfig.resolutionActions.length > 0
      ? policyConfig.resolutionActions
      : DEFAULT_RESOLUTION_ACTIONS;
  }, [policyConfig.resolutionActions]);

  // Lightweight options with descriptive guidance in dropdown menus
  const enrichedCategories = useMemo(() => {
    return (policyConfig?.categories || []).map((c) => ({
      id: c.id,
      label: c.label,
      description: c.description || CATEGORY_DESCRIPTIONS[c.id] || 'Platform policy category',
    }));
  }, [policyConfig?.categories]);

  const enrichedPatternTypes = useMemo(() => {
    return (policyConfig?.patternTypes || []).map((pt) => ({
      id: pt.id,
      label: pt.label,
      description: pt.example || PATTERN_TYPE_DESCRIPTIONS[pt.id] || 'Pattern matching method',
    }));
  }, [policyConfig?.patternTypes]);

  const enrichedSeverities = useMemo(() => {
    return (policyConfig?.severities || []).map((sev) => ({
      id: sev.id,
      label: sev.label,
      description: SEVERITY_DESCRIPTIONS[sev.id] || `${sev.label} threat severity`,
    }));
  }, [policyConfig?.severities]);

  const enrichedActions = useMemo(() => {
    return (policyConfig?.actions || []).map((act) => ({
      id: act.id,
      label: act.label,
      description: act.description || ACTION_DESCRIPTIONS[act.id] || 'Policy enforcement action',
    }));
  }, [policyConfig?.actions]);

  const enrichedResolutionActions = useMemo(() => {
    return availableResolutionActions.map((res) => ({
      id: res.id,
      label: res.label,
      description: res.description,
    }));
  }, [availableResolutionActions]);

  // Dynamic Threat Level strictly based on:
  // 1. Total awaiting incidents (unresolved)
  // 2. Total security incidents & blocked access records
  const effectiveThreatLevel = useMemo(() => {
    const pending = stats?.unresolvedIncidents ?? 0;
    const total =
      stats?.totalIncidents ??
      (Array.isArray(violations) && violations.length > 0
        ? violations.length
        : stats?.totalBlocked || 0);

    if (pending >= 5 || total >= 10) return 'critical';
    if (pending >= 1 || total >= 3) return 'elevated';
    return 'low';
  }, [stats?.unresolvedIncidents, stats?.totalIncidents, stats?.totalBlocked, violations]);

  const getDispositionMeta = useCallback(
    (actionId) => {
      return (
        availableResolutionActions.find((a) => a.id === actionId) || {
          id: actionId || 'resolved',
          label: actionId ? actionId.replace(/_/g, ' ') : 'Resolved',
          badgeClass: 'active',
          description: 'Incident investigation completed.',
        }
      );
    },
    [availableResolutionActions]
  );

  // Open Review & Resolve Modal
  const openResolveModal = (violation) => {
    setResolvingIncident(violation);
    setModalError(null);
    setResolutionForm({
      resolutionAction: availableResolutionActions[0]?.id || 'false_positive',
      notes: '',
    });
  };

  // Open View Resolution Details Modal
  const openViewModal = (violation) => {
    setViewingIncident(violation);
  };

  // Submit Formal Resolution with Disposition & Justification
  const handleConfirmResolve = async (e) => {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    if (!resolvingIncident) return;

    try {
      setActionLoading(true);
      setModalError(null);
      const res = await axios.post(`/admin/policy/violations/${resolvingIncident.id}/resolve`, {
        resolutionAction: resolutionForm.resolutionAction,
        notes: (resolutionForm.notes || '').trim(),
      });
      if (res.data?.success) {
        showNotice('Security incident formally documented and resolved');
        setResolvingIncident(null);
        await fetchViolations();
        await fetchStats();
      } else {
        const errMsg = res.data?.error || 'Failed to resolve incident';
        setModalError(errMsg);
        showNotice(errMsg, 'error');
      }
    } catch (err) {
      const errMsg = err.response?.data?.error || err.message || 'Failed to resolve incident';
      setModalError(errMsg);
      showNotice(errMsg, 'error');
    } finally {
      setActionLoading(false);
    }
  };

  // Re-open Incident for Further Security Investigation
  const executeReopenIncident = async (incidentId) => {
    if (!incidentId) return;
    try {
      setActionLoading(true);
      const res = await axios.post(`/admin/policy/violations/${incidentId}/resolve`, {
        reopen: true,
        notes: `Incident re-opened for further investigation by ${currentUser?.email || 'Superadmin'}`,
      });
      if (res.data?.success) {
        showNotice('Security incident re-opened for investigation');
        setConfirmModal(null);
        setViewingIncident(null);
        await fetchViolations();
        await fetchStats();
      } else {
        showNotice(res.data?.error || 'Failed to reopen incident', 'error');
      }
    } catch (err) {
      showNotice(err.response?.data?.error || 'Failed to reopen incident', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReopenIncident = (incidentId) => {
    if (!incidentId) return;
    setConfirmModal({
      title: 'Re-open Security Incident?',
      message: 'Are you sure you want to re-open this security incident for further investigation?',
      detail:
        'This incident will be removed from resolved history and returned to the Awaiting Review queue for SOC re-evaluation.',
      confirmText: 'Re-open Incident',
      cancelText: 'Cancel',
      variant: 'warning',
      icon: 'fa-undo',
      onConfirm: () => executeReopenIncident(incidentId),
    });
  };

  // Trigger Manual Security Audit Report
  const handleGenerateAuditReport = async () => {
    try {
      setActionLoading(true);
      const res = await axios.post('/admin/policy/audit-reports/generate', {
        date: new Date().toISOString().slice(0, 10),
      });
      if (res.data?.success) {
        showNotice('Platform security audit report generated successfully');
        await fetchAuditReports();
        await fetchStats();
      }
    } catch (err) {
      showNotice(err.response?.data?.error || 'Failed to generate audit report', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  // Reset pagination to first page when filters or tabs change
  useEffect(() => {
    setIncidentsPage(1);
  }, [searchTerm, serviceFilter, severityFilter]);

  useEffect(() => {
    setRulesPage(1);
  }, [searchTerm, categoryFilter, severityFilter]);

  useEffect(() => {
    setAuditsPage(1);
  }, [searchTerm]);

  // Filtered Incidents
  const filteredViolations = useMemo(() => {
    const list = Array.isArray(violations) ? violations : [];
    return list.filter((v) => {
      const matchSearch =
        !searchTerm ||
        (v.user_email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (v.sanitized_snippet || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (Array.isArray(v.matched_keywords) ? v.matched_keywords : []).some((k) =>
          (k || '').toLowerCase().includes(searchTerm.toLowerCase())
        );

      const matchService = !serviceFilter || v.service_name === serviceFilter;
      const matchSeverity = !severityFilter || v.severity === severityFilter;

      return matchSearch && matchService && matchSeverity;
    });
  }, [violations, searchTerm, serviceFilter, severityFilter]);

  // Paginated Violations (Tab 1)
  const paginatedViolations = useMemo(() => {
    const start = (incidentsPage - 1) * incidentsPerPage;
    return filteredViolations.slice(start, start + incidentsPerPage);
  }, [filteredViolations, incidentsPage, incidentsPerPage]);

  // Filtered Rules (Predefined rules always put at last, custom rules at top)
  const filteredRules = useMemo(() => {
    const list = Array.isArray(rules) ? rules : [];
    return list
      .filter((r) => {
        const matchSearch =
          !searchTerm ||
          (r.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
          (r.pattern || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
          (r.description || '').toLowerCase().includes(searchTerm.toLowerCase());

        const matchCategory = !categoryFilter || r.category === categoryFilter;
        const matchSeverity = !severityFilter || r.severity === severityFilter;

        return matchSearch && matchCategory && matchSeverity;
      })
      .sort((a, b) => {
        // Predefined always put at last
        if (a.is_system && !b.is_system) return 1;
        if (!a.is_system && b.is_system) return -1;
        return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      });
  }, [rules, searchTerm, categoryFilter, severityFilter]);

  // Paginated Rules (Tab 2)
  const paginatedRules = useMemo(() => {
    const start = (rulesPage - 1) * rulesPerPage;
    return filteredRules.slice(start, start + rulesPerPage);
  }, [filteredRules, rulesPage, rulesPerPage]);

  // Filtered Audits
  const filteredAudits = useMemo(() => {
    const list = Array.isArray(auditReports) ? auditReports : [];
    return list.filter((rep) => {
      if (!searchTerm) return true;
      const term = searchTerm.toLowerCase();
      return (
        (rep.report_date || '').toLowerCase().includes(term) ||
        (rep.summary || '').toLowerCase().includes(term) ||
        (rep.threat_level || '').toLowerCase().includes(term) ||
        (rep.generated_by || '').toLowerCase().includes(term)
      );
    });
  }, [auditReports, searchTerm]);

  // Paginated Audits (Tab 3)
  const paginatedAudits = useMemo(() => {
    const start = (auditsPage - 1) * auditsPerPage;
    return filteredAudits.slice(start, start + auditsPerPage);
  }, [filteredAudits, auditsPage, auditsPerPage]);

  return (
    <div className="policy-checkpoint-module">
      {/* ── Top Module Header (Lead Huntrix Unified Page Banner) ── */}
      <Banner
        title="Policy Checkpoint & Security Automation"
        subtitle="Autonomous server-side policy enforcement: scans all incoming prompts and model completions across all LLM/AI services for sensitive keywords, blocks invalid access, alerts superadmins, and runs automated daily security audits."
        type="page-header"
      />

      {notification && (
        <div className={`pc-alert-banner ${notification.type}`}>
          <i
            className={
              notification.type === 'error' ? 'fas fa-exclamation-circle' : 'fas fa-check-circle'
            }
          ></i>
          <span>{notification.msg}</span>
        </div>
      )}

      {/* ── KPI Metric Cards ── */}
      <div className="pc-kpi-row">
        <div className="pc-kpi-card">
          <div className="pc-kpi-label">Platform Threat Level</div>
          <div className="pc-kpi-metric-row">
            <span
              className={`pc-threat-pill ${effectiveThreatLevel}`}
              title="Threat Rules: Critical (≥5 awaiting or ≥10 total), Elevated (1-4 awaiting or 3-9 total), Low (0 awaiting & <3 total)"
            >
              <i className="fas fa-shield-alt"></i>
              {effectiveThreatLevel.toUpperCase()}
            </span>
          </div>
          <div className="pc-kpi-help">
            {(() => {
              const pending = stats?.unresolvedIncidents ?? 0;
              const total =
                stats?.totalIncidents ??
                (Array.isArray(violations) && violations.length > 0
                  ? violations.length
                  : stats?.totalBlocked || 0);
              const resolved = stats?.resolvedIncidents ?? Math.max(0, total - pending);

              if (total === 0) {
                return 'All clear • No security incidents recorded';
              }
              if (pending === 0) {
                return `All clear • 0 remaining (${resolved} of ${total} resolved)`;
              }
              return `${pending} remaining to review • ${resolved} resolved of ${total} total incident${total === 1 ? '' : 's'}`;
            })()}
          </div>
        </div>

        <div className="pc-kpi-card">
          <div className="pc-kpi-label">AI Scans Evaluated (Today)</div>
          <div className="pc-kpi-metric-row">
            <div className="pc-kpi-number">{stats?.totalScans ?? 0}</div>
          </div>
          <div className="pc-kpi-help">Scanned across Assistant, Analysis, LLM</div>
        </div>

        <div className="pc-kpi-card">
          <div className="pc-kpi-label">Unauthorized Requests Blocked</div>
          <div className="pc-kpi-metric-row">
            <div
              className="pc-kpi-number"
              style={{ color: (stats?.totalBlocked ?? 0) > 0 ? '#dc2626' : '#059669' }}
            >
              {stats?.totalBlocked ?? 0}
            </div>
          </div>
          <div className="pc-kpi-help">Intercepted before touching models</div>
        </div>

        <div className="pc-kpi-card">
          <div className="pc-kpi-label">Active Sensitive Rules</div>
          <div className="pc-kpi-metric-row">
            <div className="pc-kpi-number">{stats?.activeRules ?? 0}</div>
          </div>
          <div className="pc-kpi-help">Compiled in-memory regex & keywords</div>
        </div>

        <div className="pc-kpi-card">
          <div className="pc-kpi-label">Pending Incidents for Review</div>
          <div className="pc-kpi-metric-row">
            <div
              className="pc-kpi-number"
              style={{ color: (stats?.unresolvedIncidents ?? 0) > 0 ? '#d97706' : '#64748b' }}
            >
              {stats?.unresolvedIncidents ?? 0}
            </div>
          </div>
          <div className="pc-kpi-help">Awaiting Superadmin resolution</div>
        </div>
      </div>

      {/* ── Section Tabs Navigation ── */}
      <div className="pc-section-tabs">
        <button
          className={`pc-section-tab ${activeTab === 'incidents' ? 'active' : ''}`}
          onClick={() => setActiveTab('incidents')}
        >
          <i className="fas fa-shield-virus"></i>
          Security Incidents & Blocked Access ({filteredViolations.length})
        </button>
        <button
          className={`pc-section-tab ${activeTab === 'rules' ? 'active' : ''}`}
          onClick={() => setActiveTab('rules')}
        >
          <i className="fas fa-key"></i>
          Sensitive Keyword Rules ({filteredRules.length})
        </button>
        <button
          className={`pc-section-tab ${activeTab === 'audits' ? 'active' : ''}`}
          onClick={() => setActiveTab('audits')}
        >
          <i className="fas fa-file-invoice"></i>
          Daily Platform Security Audits ({filteredAudits.length})
        </button>
      </div>

      {/* ── Filter Toolbar ── */}
      <div className="pc-toolbar">
        <div className="pc-search-box">
          <i className="fas fa-search pc-search-icon"></i>
          <input
            type="text"
            className="pc-search-input"
            placeholder={
              activeTab === 'incidents'
                ? 'Search by user, email, matched keyword...'
                : activeTab === 'rules'
                  ? 'Search rule name or pattern...'
                  : 'Search audit summaries...'
            }
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="pc-filter-group">
          {activeTab === 'incidents' && (
            <select
              className="pc-select"
              value={serviceFilter}
              onChange={(e) => setServiceFilter(e.target.value)}
            >
              <option value="">All Monitored Services</option>
              {(policyConfig?.services || []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.label}
                </option>
              ))}
            </select>
          )}

          {activeTab === 'rules' && (
            <select
              className="pc-select"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <option value="">All Policy Categories</option>
              {(policyConfig?.categories || []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label}
                </option>
              ))}
            </select>
          )}

          {(activeTab === 'incidents' || activeTab === 'rules') && (
            <select
              className="pc-select"
              value={severityFilter}
              onChange={(e) => setSeverityFilter(e.target.value)}
            >
              <option value="">All Severities</option>
              {(policyConfig?.severities || []).map((sev) => (
                <option key={sev.id} value={sev.id}>
                  {sev.label} Severity
                </option>
              ))}
            </select>
          )}

          {/* Table Header Action: Rules tab */}
          {activeTab === 'rules' && (
            <button
              type="button"
              className="pc-btn pc-btn-primary"
              onClick={() => setShowAddRuleModal(true)}
              title="Add a new sensitive keyword or pattern rule"
            >
              <i className="fas fa-plus"></i>
              <span>Add Sensitive Keyword</span>
            </button>
          )}

          {/* Table Header Action: Daily Audits tab */}
          {activeTab === 'audits' && (
            <button
              type="button"
              className="pc-btn pc-btn-success"
              onClick={handleGenerateAuditReport}
              disabled={actionLoading}
              title="Compile and archive platform security audit report"
            >
              <i className={`fas fa-file-contract ${actionLoading ? 'fa-spin' : ''}`}></i>
              <span>Run Security Audit Now</span>
            </button>
          )}
        </div>
      </div>

      {/* ── TAB 1: Live Security Incidents ── */}
      {activeTab === 'incidents' && (
        <div className="pc-card-table">
          {filteredViolations.length === 0 ? (
            <div className="pc-empty-state">
              <div className={`pc-empty-icon-wrap ${violations.length === 0 ? 'success' : ''}`}>
                <i className={violations.length === 0 ? 'fas fa-shield-alt' : 'fas fa-search'}></i>
              </div>
              <h3 className="pc-empty-title">
                {violations.length === 0
                  ? 'All Clear — No Security Incidents'
                  : 'No Incidents Match Search Criteria'}
              </h3>
              <p className="pc-empty-desc">
                {violations.length === 0
                  ? 'All prompt queries and model completions across all monitored AI services are currently compliant with platform security policies.'
                  : 'No security incidents match the selected search term or filters. Try adjusting or clearing your filters.'}
              </p>
              {violations.length > 0 && (
                <button
                  className="pc-btn pc-btn-outline"
                  onClick={() => {
                    setSearchTerm('');
                    setServiceFilter('');
                    setSeverityFilter('');
                  }}
                >
                  <i className="fas fa-times"></i> Clear Filters
                </button>
              )}
            </div>
          ) : (
            <>
              <div className="pc-table-responsive">
                <table className="pc-table">
                  <thead>
                    <tr>
                      <th>Timestamp (UTC)</th>
                      <th>Service</th>
                      <th>Actor / Account</th>
                      <th>Triggered Rule</th>
                      <th>Severity</th>
                      <th>Action</th>
                      <th>Sanitized Context Excerpt</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(paginatedViolations || []).map((v) => (
                      <tr key={v.id}>
                        <td style={{ whiteSpace: 'nowrap', fontSize: '12px', color: '#64748b' }}>
                          {new Date(v.created_at).toLocaleString()}
                        </td>
                        <td>
                          <span className="pc-badge-service">{v.service_name}</span>
                        </td>
                        <td>
                          <div style={{ fontWeight: 600, color: '#0f172a' }}>
                            {v.user_email || 'Unauthenticated'}
                          </div>
                          <div style={{ fontSize: '11px', color: '#94a3b8' }}>
                            IP: {v.ip_address || 'Internal Gateway'}
                          </div>
                        </td>
                        <td>
                          {(() => {
                            const rawKeywords = Array.isArray(v.matched_keywords)
                              ? v.matched_keywords.filter(Boolean)
                              : [];
                            if (rawKeywords.length === 0) {
                              return <span style={{ color: '#94a3b8' }}>—</span>;
                            }
                            if (rawKeywords.length === 1) {
                              return (
                                <span className="pc-badge-pill high" style={{ margin: '2px' }}>
                                  {rawKeywords[0]}
                                </span>
                              );
                            }
                            const isExpanded = !!expandedRulesMap[v.id];
                            const remaining = rawKeywords.length - 1;
                            return (
                              <div
                                className={`pc-rules-pill-group ${isExpanded ? 'expanded' : ''}`}
                              >
                                {!isExpanded ? (
                                  <>
                                    <span className="pc-badge-pill high" style={{ margin: '2px' }}>
                                      {rawKeywords[0]}
                                    </span>
                                    <button
                                      type="button"
                                      className="pc-more-rules-btn"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        toggleExpandRules(v.id);
                                      }}
                                      title={`Additional triggered rules:\n• ${rawKeywords.slice(1).join('\n• ')}`}
                                      aria-label={`Show ${remaining} more rules`}
                                    >
                                      +{remaining} more
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    {rawKeywords.map((k, i) => (
                                      <span
                                        key={i}
                                        className="pc-badge-pill high"
                                        style={{ margin: '2px' }}
                                      >
                                        {k}
                                      </span>
                                    ))}
                                    <button
                                      type="button"
                                      className="pc-more-rules-btn collapse"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        toggleExpandRules(v.id);
                                      }}
                                      aria-label="Collapse rules"
                                    >
                                      Hide
                                    </button>
                                  </>
                                )}
                              </div>
                            );
                          })()}
                        </td>
                        <td>
                          <span
                            className={`pc-badge-pill ${(v.severity || 'medium').toLowerCase()}`}
                          >
                            {v.severity}
                          </span>
                        </td>
                        <td>
                          <span className={`pc-badge-pill ${v.action_taken}`}>
                            {v.action_taken}
                          </span>
                        </td>
                        <td>
                          <span className="pc-snippet-box">
                            {v.sanitized_snippet || '[REDACTED]'}
                          </span>
                        </td>
                        <td>
                          {v.is_resolved ? (
                            (() => {
                              const disp = getDispositionMeta(v.resolution_action);
                              return (
                                <span
                                  className={`pc-badge-pill ${disp.badgeClass || 'active'}`}
                                  title={disp.description || 'Resolved'}
                                  style={{ whiteSpace: 'nowrap' }}
                                >
                                  <i
                                    className="fas fa-check-circle"
                                    style={{ marginRight: '5px' }}
                                  ></i>
                                  {disp.label.split('(')[0].trim()}
                                </span>
                              );
                            })()
                          ) : (
                            <span className="pc-badge-pill high" style={{ whiteSpace: 'nowrap' }}>
                              <i className="fas fa-clock" style={{ marginRight: '5px' }}></i>
                              Pending Review
                            </span>
                          )}
                        </td>
                        <td>
                          {!v.is_resolved ? (
                            <button
                              className="pc-btn pc-btn-primary"
                              style={{
                                padding: '5px 12px',
                                fontSize: '11.5px',
                                whiteSpace: 'nowrap',
                              }}
                              onClick={() => openResolveModal(v)}
                              title="Review incident evidence and document investigation disposition"
                            >
                              <i className="fas fa-clipboard-check"></i> Review &amp; Resolve
                            </button>
                          ) : (
                            <button
                              className="pc-btn pc-btn-outline"
                              style={{
                                padding: '5px 12px',
                                fontSize: '11.5px',
                                whiteSpace: 'nowrap',
                              }}
                              onClick={() => openViewModal(v)}
                              title="View incident investigation details and resolution audit history"
                            >
                              <i className="fas fa-eye"></i> View Resolution
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pagination
                currentPage={incidentsPage}
                totalPages={Math.max(1, Math.ceil(filteredViolations.length / incidentsPerPage))}
                onPageChange={setIncidentsPage}
                itemsPerPage={incidentsPerPage}
                onItemsPerPageChange={setIncidentsPerPage}
                totalItems={filteredViolations.length}
                itemsName="incidents"
                rowOptions={[5, 10, 20, 50]}
              />
            </>
          )}
        </div>
      )}

      {/* ── TAB 2: Sensitive Keyword Rules ── */}
      {activeTab === 'rules' && (
        <div className="pc-card-table">
          {filteredRules.length === 0 ? (
            <div className="pc-empty-state">
              <div className="pc-empty-icon-wrap">
                <i className={rules.length === 0 ? 'fas fa-key' : 'fas fa-search'}></i>
              </div>
              <h3 className="pc-empty-title">
                {rules.length === 0
                  ? 'No Sensitive Keyword Rules'
                  : 'No Rules Match Filter Criteria'}
              </h3>
              <p className="pc-empty-desc">
                {rules.length === 0
                  ? 'Configure sensitive keywords, credit card patterns, or prompt injection regexes to safeguard AI models.'
                  : 'No rules matched your search or category filters. Try clearing your filters or creating a new rule.'}
              </p>
              {rules.length > 0 ? (
                <button
                  className="pc-btn pc-btn-outline"
                  onClick={() => {
                    setSearchTerm('');
                    setCategoryFilter('');
                    setSeverityFilter('');
                  }}
                >
                  <i className="fas fa-times"></i> Clear Filters
                </button>
              ) : (
                <button className="pc-btn pc-btn-primary" onClick={() => setShowAddRuleModal(true)}>
                  <i className="fas fa-plus"></i> Add Defense Rule
                </button>
              )}
            </div>
          ) : (
            <>
              <div className="pc-table-responsive">
                <table className="pc-table">
                  <thead>
                    <tr>
                      <th>Rule Name</th>
                      <th>Pattern & Evaluation Type</th>
                      <th>Policy Category</th>
                      <th>Severity</th>
                      <th>Enforcement</th>
                      <th>Active Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(paginatedRules || []).map((r) => (
                      <tr key={r.id}>
                        <td>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              flexWrap: 'wrap',
                            }}
                          >
                            <span style={{ fontWeight: 600, color: '#0f172a' }}>{r.name}</span>
                            {r.is_system && (
                              <span
                                className="pc-badge-system"
                                title="Predefined platform security policy (Protected)"
                              >
                                <i className="fas fa-shield-alt"></i> Predefined
                              </span>
                            )}
                          </div>
                          {r.description && (
                            <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                              {r.description}
                            </div>
                          )}
                        </td>
                        <td>
                          <span className="pc-snippet-box" style={{ color: '#2563eb' }}>
                            {r.pattern}
                          </span>
                          <span style={{ fontSize: '11px', color: '#94a3b8' }}>
                            Type: {r.pattern_type}
                          </span>
                        </td>
                        <td>
                          <span className="pc-badge-service">{r.category}</span>
                        </td>
                        <td>
                          <span className={`pc-badge-pill ${r.severity}`}>{r.severity}</span>
                        </td>
                        <td>
                          <span className={`pc-badge-pill ${r.action}`}>{r.action}</span>
                        </td>
                        <td>
                          {r.is_system ? (
                            <span
                              className="pc-badge-pill active"
                              title="Mandatory platform security guardrail — always active"
                            >
                              <i
                                className="fas fa-check-circle"
                                style={{ fontSize: '10px', marginRight: '4px' }}
                              ></i>
                              Always Active
                            </span>
                          ) : (
                            <span
                              className={`pc-badge-pill ${r.is_active ? 'active' : 'disabled'}`}
                            >
                              {r.is_active ? 'Active' : 'Disabled'}
                            </span>
                          )}
                        </td>
                        <td>
                          {r.is_system ? (
                            <span
                              className="pc-system-locked-tag"
                              title="Predefined platform security policy is a mandatory platform guardrail. It cannot be disabled or deleted."
                            >
                              <i className="fas fa-shield-alt"></i> Mandatory Policy
                            </span>
                          ) : (
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                              <button
                                className="pc-btn pc-btn-outline"
                                style={{ padding: '4px 8px', fontSize: '11px' }}
                                onClick={() => handleToggleRule(r)}
                                title={r.is_active ? 'Disable custom rule' : 'Enable custom rule'}
                              >
                                {r.is_active ? 'Disable' : 'Enable'}
                              </button>
                              <button
                                className="pc-btn pc-btn-danger-outline"
                                style={{ padding: '4px 8px', fontSize: '11px' }}
                                onClick={() => handleDeleteRule(r.id, r.name, r.is_system)}
                                title="Delete custom rule"
                              >
                                <i className="fas fa-trash-alt"></i>
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pagination
                currentPage={rulesPage}
                totalPages={Math.max(1, Math.ceil(filteredRules.length / rulesPerPage))}
                onPageChange={setRulesPage}
                itemsPerPage={rulesPerPage}
                onItemsPerPageChange={setRulesPerPage}
                totalItems={filteredRules.length}
                itemsName="rules"
                rowOptions={[5, 10, 20, 50]}
              />
            </>
          )}
        </div>
      )}

      {/* ── TAB 3: Daily Platform Security Audits ── */}
      {activeTab === 'audits' && (
        <div className="pc-card-table">
          {filteredAudits.length === 0 ? (
            <div className="pc-empty-state">
              <div className="pc-empty-icon-wrap">
                <i
                  className={auditReports.length === 0 ? 'fas fa-file-contract' : 'fas fa-search'}
                ></i>
              </div>
              <h3 className="pc-empty-title">
                {auditReports.length === 0
                  ? 'No Daily Security Audits Archived'
                  : 'No Audit Reports Match Search'}
              </h3>
              <p className="pc-empty-desc">
                {auditReports.length === 0
                  ? 'The automated security audit engine compiles daily platform threat reports every 24 hours. You can also generate one on-demand now.'
                  : 'No archived audit reports matched your search term.'}
              </p>
              {auditReports.length > 0 ? (
                <button className="pc-btn pc-btn-outline" onClick={() => setSearchTerm('')}>
                  <i className="fas fa-times"></i> Clear Search
                </button>
              ) : (
                <button
                  className="pc-btn pc-btn-success"
                  onClick={handleGenerateAuditReport}
                  disabled={actionLoading}
                >
                  <i className="fas fa-play"></i> Run Security Audit Now
                </button>
              )}
            </div>
          ) : (
            <>
              <div className="pc-table-responsive">
                <table className="pc-table">
                  <thead>
                    <tr>
                      <th>Audit Date</th>
                      <th>Threat Assessment</th>
                      <th>Total AI Scans</th>
                      <th>Violations Intercepted</th>
                      <th>Blocked Access</th>
                      <th>Generator Source</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(paginatedAudits || []).map((rep) => (
                      <tr key={rep.id}>
                        <td style={{ fontWeight: 600, color: '#0f172a' }}>{rep.report_date}</td>
                        <td>
                          <span className={`pc-threat-pill ${rep.threat_level || 'low'}`}>
                            {rep.threat_level}
                          </span>
                        </td>
                        <td>{rep.total_scans}</td>
                        <td style={{ color: rep.total_violations > 0 ? '#d97706' : '#64748b' }}>
                          {rep.total_violations}
                        </td>
                        <td
                          style={{
                            color: rep.total_blocked > 0 ? '#dc2626' : '#059669',
                            fontWeight: 600,
                          }}
                        >
                          {rep.total_blocked}
                        </td>
                        <td style={{ fontSize: '12px', color: '#64748b' }}>{rep.generated_by}</td>
                        <td>
                          <button
                            className="pc-btn pc-btn-outline"
                            style={{ padding: '4px 10px', fontSize: '11px' }}
                            onClick={() => setSelectedReport(rep)}
                          >
                            <i className="fas fa-eye"></i> View Summary
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pagination
                currentPage={auditsPage}
                totalPages={Math.max(1, Math.ceil(filteredAudits.length / auditsPerPage))}
                onPageChange={setAuditsPage}
                itemsPerPage={auditsPerPage}
                onItemsPerPageChange={setAuditsPerPage}
                totalItems={filteredAudits.length}
                itemsName="audit reports"
                rowOptions={[5, 10, 20, 50]}
              />
            </>
          )}
        </div>
      )}

      {/* ── Bottom Viewport Breathing Room ── */}
      <div className="pc-bottom-spacer" />

      {/* ── Modal: Add Sensitive Keyword Rule ── */}
      {showAddRuleModal && (
        <div className="pc-modal-overlay">
          <div className="pc-modal-card">
            <div className="pc-modal-header">
              <h3>Add Sensitive Keyword / Pattern Rule</h3>
              <button className="pc-modal-close-btn" onClick={() => setShowAddRuleModal(false)}>
                &times;
              </button>
            </div>
            <form onSubmit={handleCreateRule}>
              <div className="pc-modal-content">
                <div className="pc-field-group">
                  <label className="pc-field-label">Rule Name *</label>
                  <input
                    type="text"
                    className="pc-field-input"
                    placeholder="e.g. OpenAI Secret Key Scanner"
                    value={newRule.name}
                    onChange={(e) => setNewRule({ ...newRule, name: e.target.value })}
                    required
                  />
                </div>

                <div className="pc-field-group">
                  <label className="pc-field-label">Pattern (RegEx or Keyword) *</label>
                  <input
                    type="text"
                    className="pc-field-input"
                    placeholder="e.g. sk-[a-zA-Z0-9]{32,}"
                    value={newRule.pattern}
                    onChange={(e) => setNewRule({ ...newRule, pattern: e.target.value })}
                    required
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                  <div className="pc-field-group">
                    <label className="pc-field-label">Pattern Type</label>
                    <PolicyCustomSelect
                      value={newRule.pattern_type}
                      onChange={(val) => setNewRule({ ...newRule, pattern_type: val })}
                      options={enrichedPatternTypes}
                      id="pcs-pattern-type"
                    />
                  </div>

                  <div className="pc-field-group">
                    <label className="pc-field-label">Category</label>
                    <PolicyCustomSelect
                      value={newRule.category}
                      onChange={(val) => setNewRule({ ...newRule, category: val })}
                      options={enrichedCategories}
                      id="pcs-category"
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                  <div className="pc-field-group">
                    <label className="pc-field-label">Severity Level</label>
                    <PolicyCustomSelect
                      value={newRule.severity}
                      onChange={(val) => setNewRule({ ...newRule, severity: val })}
                      options={enrichedSeverities}
                      id="pcs-severity"
                    />
                  </div>

                  <div className="pc-field-group">
                    <label className="pc-field-label">Enforcement Action</label>
                    <PolicyCustomSelect
                      value={newRule.action}
                      onChange={(val) => setNewRule({ ...newRule, action: val })}
                      options={enrichedActions}
                      id="pcs-action"
                    />
                  </div>
                </div>

                <div className="pc-field-group">
                  <label className="pc-field-label">Policy Justification / Description</label>
                  <textarea
                    className="pc-field-textarea"
                    rows="3"
                    placeholder="Describe why this keyword pattern is prohibited across LLM/AI services"
                    value={newRule.description}
                    onChange={(e) => setNewRule({ ...newRule, description: e.target.value })}
                  />
                </div>
              </div>

              <div className="pc-modal-footer">
                <button
                  type="button"
                  className="pc-btn pc-btn-outline"
                  onClick={() => setShowAddRuleModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="pc-btn pc-btn-primary" disabled={actionLoading}>
                  {actionLoading ? 'Compiling Rule...' : 'Save & Compile Rule'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Modal: View Audit Report ── */}
      {selectedReport && (
        <div className="pc-modal-overlay">
          <div className="pc-modal-card" style={{ maxWidth: '720px' }}>
            <div className="pc-modal-header">
              <h3>Security Audit Report: {selectedReport.report_date}</h3>
              <button className="pc-modal-close-btn" onClick={() => setSelectedReport(null)}>
                &times;
              </button>
            </div>
            <div className="pc-modal-content">
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                <span className={`pc-threat-pill ${selectedReport.threat_level || 'low'}`}>
                  {selectedReport.threat_level} THREAT
                </span>
                <span style={{ color: '#64748b', fontSize: '13px' }}>
                  Audited on: {new Date(selectedReport.created_at).toLocaleString()}
                </span>
              </div>

              <div
                style={{
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  padding: '16px',
                  borderRadius: '10px',
                }}
              >
                <div style={{ fontWeight: 700, color: '#0f172a', marginBottom: '8px' }}>
                  Executive Security Assessment:
                </div>
                <div
                  style={{
                    whiteSpace: 'pre-line',
                    fontSize: '13px',
                    color: '#334155',
                    lineHeight: 1.6,
                  }}
                >
                  {selectedReport.summary}
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div
                  style={{
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    padding: '14px',
                    borderRadius: '8px',
                  }}
                >
                  <div
                    style={{
                      fontSize: '11px',
                      fontWeight: 700,
                      color: '#64748b',
                      textTransform: 'uppercase',
                      marginBottom: '8px',
                    }}
                  >
                    Severity Distribution
                  </div>
                  {Object.entries(selectedReport?.severity_breakdown || {}).map(([sev, count]) => (
                    <div
                      key={sev}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        padding: '4px 0',
                        fontSize: '13px',
                      }}
                    >
                      <span className={`pc-badge-pill ${sev}`}>{sev}</span>
                      <span style={{ fontWeight: 700, color: '#0f172a' }}>{count}</span>
                    </div>
                  ))}
                </div>

                <div
                  style={{
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    padding: '14px',
                    borderRadius: '8px',
                  }}
                >
                  <div
                    style={{
                      fontSize: '11px',
                      fontWeight: 700,
                      color: '#64748b',
                      textTransform: 'uppercase',
                      marginBottom: '8px',
                    }}
                  >
                    Affected AI Services
                  </div>
                  {Object.keys(selectedReport.service_breakdown || {}).length === 0 ? (
                    <div style={{ fontSize: '12px', color: '#94a3b8' }}>None</div>
                  ) : (
                    Object.entries(selectedReport.service_breakdown || {}).map(([srv, count]) => (
                      <div
                        key={srv}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          padding: '4px 0',
                          fontSize: '13px',
                        }}
                      >
                        <span style={{ color: '#64748b' }}>{srv}</span>
                        <span style={{ fontWeight: 700, color: '#0f172a' }}>{count}</span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
            <div className="pc-modal-footer">
              <button className="pc-btn pc-btn-outline" onClick={() => setSelectedReport(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: Review & Resolve Security Incident ── */}
      {resolvingIncident && (
        <div className="pc-modal-overlay" onClick={() => setResolvingIncident(null)}>
          <div
            className="pc-modal-card"
            style={{ maxWidth: '680px' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="pc-modal-header">
              <div>
                <h3
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: '16px',
                    fontWeight: 600,
                    color: '#0f172a',
                  }}
                >
                  <i className="fas fa-shield-alt" style={{ color: '#475569' }}></i>
                  Incident Investigation &amp; Resolution
                </h3>
                <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                  Case ID: #{resolvingIncident.id.slice(0, 8)} · Monitored Service:{' '}
                  <strong>{resolvingIncident.service_name}</strong>
                </div>
              </div>
              <button
                className="pc-modal-close-btn"
                onClick={() => setResolvingIncident(null)}
                title="Close"
              >
                &times;
              </button>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleConfirmResolve(e);
              }}
            >
              <div className="pc-modal-content">
                {modalError && (
                  <div className="pc-modal-error-banner">
                    <i className="fas fa-exclamation-circle" style={{ marginRight: '6px' }}></i>
                    <span>{modalError}</span>
                  </div>
                )}
                {/* Incident Overview Box */}
                <div className="pc-incident-info-box">
                  <div className="pc-incident-info-grid">
                    <div>
                      <div className="pc-incident-info-label">Actor / Account</div>
                      <div className="pc-incident-info-val">
                        {resolvingIncident.user_email || 'Unauthenticated'}
                      </div>
                      <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '1px' }}>
                        IP: {resolvingIncident.ip_address || 'Internal Gateway'}
                      </div>
                    </div>
                    <div>
                      <div className="pc-incident-info-label">Detected At (UTC)</div>
                      <div className="pc-incident-info-val" style={{ fontSize: '12px' }}>
                        {new Date(resolvingIncident.created_at).toLocaleString()}
                      </div>
                    </div>
                    <div>
                      <div className="pc-incident-info-label">Severity &amp; Action</div>
                      <div
                        style={{
                          display: 'flex',
                          gap: '6px',
                          alignItems: 'center',
                          marginTop: '2px',
                        }}
                      >
                        <span
                          className={`pc-badge-subtle ${(resolvingIncident.severity || 'medium').toLowerCase()}`}
                        >
                          {resolvingIncident.severity}
                        </span>
                        <span className={`pc-badge-subtle ${resolvingIncident.action_taken}`}>
                          {resolvingIncident.action_taken}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Triggered Rules — Clean slate tags */}
                  <div
                    style={{
                      marginTop: '14px',
                      paddingTop: '12px',
                      borderTop: '1px solid #f1f5f9',
                    }}
                  >
                    <div className="pc-incident-info-label" style={{ marginBottom: '6px' }}>
                      Triggered Rules
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                      {(resolvingIncident.matched_keywords || []).map((k, i) => (
                        <span key={i} className="pc-rule-tag">
                          {k}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Sanitized Excerpt snippet — Sleek dark code block */}
                  <div
                    style={{
                      marginTop: '14px',
                      paddingTop: '12px',
                      borderTop: '1px solid #f1f5f9',
                    }}
                  >
                    <div className="pc-incident-info-label" style={{ marginBottom: '6px' }}>
                      Sanitized Context Excerpt (Payload Evidence):
                    </div>
                    <pre className="pc-incident-snippet-pre">
                      {resolvingIncident.sanitized_snippet || '[REDACTED CONTENT]'}
                    </pre>
                  </div>
                </div>

                {/* Disposition Selection */}
                <div className="pc-field-group">
                  <label className="pc-field-label">
                    Investigation Disposition / Outcome <span style={{ color: '#dc2626' }}>*</span>
                  </label>
                  <PolicyCustomSelect
                    value={resolutionForm.resolutionAction}
                    onChange={(val) =>
                      setResolutionForm((prev) => ({
                        ...prev,
                        resolutionAction: val,
                      }))
                    }
                    options={enrichedResolutionActions}
                    id="pcs-resolution-action"
                  />
                  {/* Subtle disposition hint text — No heavy blue banner */}
                  {(() => {
                    const sel = availableResolutionActions.find(
                      (a) => a.id === resolutionForm.resolutionAction
                    );
                    return sel?.description ? (
                      <p className="pc-disposition-hint-text">{sel.description}</p>
                    ) : null;
                  })()}
                </div>

                {/* Justification & Investigation Notes */}
                <div className="pc-field-group">
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <label className="pc-field-label" style={{ marginBottom: 0 }}>
                      Investigation Findings &amp; Justification
                    </label>
                    <span style={{ fontSize: '11px', color: '#94a3b8' }}>Optional</span>
                  </div>
                  <textarea
                    className="pc-field-textarea"
                    rows={3}
                    placeholder="Optional: Provide root-cause summary, user interview notes, firewall/secret rotation status, or business justification..."
                    value={resolutionForm.notes}
                    onChange={(e) =>
                      setResolutionForm((prev) => ({ ...prev, notes: e.target.value }))
                    }
                  />
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      fontSize: '11px',
                      color: '#94a3b8',
                    }}
                  >
                    <span>{resolutionForm.notes.length} characters</span>
                  </div>
                </div>
              </div>

              <div className="pc-modal-footer">
                <button
                  type="button"
                  className="pc-btn pc-btn-outline"
                  onClick={() => setResolvingIncident(null)}
                  disabled={actionLoading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirmResolve}
                  className="pc-btn pc-btn-success"
                  disabled={actionLoading}
                >
                  <i className={`fas fa-check-double ${actionLoading ? 'fa-spin' : ''}`}></i>
                  <span>Document &amp; Resolve Incident</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Modal: View Resolution Details & Audit Trail ── */}
      {viewingIncident && (
        <div className="pc-modal-overlay" onClick={() => setViewingIncident(null)}>
          <div
            className="pc-modal-card"
            style={{ maxWidth: '680px' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="pc-modal-header">
              <div>
                <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <i className="fas fa-file-shield" style={{ color: '#2563eb' }}></i>
                  Incident Resolution Record &amp; Audit Trail
                </h3>
                <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                  Case ID: #{viewingIncident.id.slice(0, 8)} • Service:{' '}
                  <strong>{viewingIncident.service_name}</strong>
                </div>
              </div>
              <button
                className="pc-modal-close-btn"
                onClick={() => setViewingIncident(null)}
                title="Close"
              >
                &times;
              </button>
            </div>

            <div className="pc-modal-content">
              {/* Incident Resolution Status Bar */}
              <div className="pc-incident-status-bar">
                <div className="pc-status-bar-left">
                  {(() => {
                    const disp = getDispositionMeta(viewingIncident.resolution_action);
                    return (
                      <>
                        <span className="pc-badge-subtle resolved">
                          <i
                            className="fas fa-check-circle"
                            style={{ marginRight: '5px', color: '#059669' }}
                          ></i>
                          Resolved
                        </span>
                        <span className="pc-disposition-pill">{disp.label}</span>
                      </>
                    );
                  })()}
                </div>
                <div className="pc-status-bar-right">
                  Resolved by <strong>{viewingIncident.resolved_by || 'Superadmin'}</strong> •{' '}
                  {viewingIncident.resolved_at
                    ? new Date(viewingIncident.resolved_at).toLocaleString()
                    : 'Recorded Date'}
                </div>
              </div>

              {/* Optional Investigation Notes (only rendered when justification exists) */}
              {viewingIncident.resolution_notes && viewingIncident.resolution_notes.trim() && (
                <div className="pc-incident-notes-box">
                  <div className="pc-incident-notes-label">Investigation Justification:</div>
                  <div className="pc-incident-notes-body">{viewingIncident.resolution_notes}</div>
                </div>
              )}

              {/* Original Incident Context */}
              <div className="pc-incident-info-box">
                <div className="pc-incident-info-grid">
                  <div>
                    <div className="pc-incident-info-label">Actor / Account</div>
                    <div className="pc-incident-info-val">
                      {viewingIncident.user_email || 'Unauthenticated'}
                    </div>
                    <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '1px' }}>
                      IP: {viewingIncident.ip_address || 'Internal Gateway'}
                    </div>
                  </div>
                  <div>
                    <div className="pc-incident-info-label">Detected At (UTC)</div>
                    <div className="pc-incident-info-val" style={{ fontSize: '12px' }}>
                      {new Date(viewingIncident.created_at).toLocaleString()}
                    </div>
                  </div>
                  <div>
                    <div className="pc-incident-info-label">Severity &amp; Action</div>
                    <div
                      style={{
                        display: 'flex',
                        gap: '6px',
                        alignItems: 'center',
                        marginTop: '2px',
                      }}
                    >
                      <span
                        className={`pc-badge-subtle ${(viewingIncident.severity || 'medium').toLowerCase()}`}
                      >
                        {viewingIncident.severity}
                      </span>
                      <span className={`pc-badge-subtle ${viewingIncident.action_taken}`}>
                        {viewingIncident.action_taken}
                      </span>
                    </div>
                  </div>
                </div>

                {Array.isArray(viewingIncident.matched_keywords) &&
                  viewingIncident.matched_keywords.length > 0 && (
                    <div
                      style={{
                        marginTop: '14px',
                        paddingTop: '12px',
                        borderTop: '1px solid #f1f5f9',
                      }}
                    >
                      <div className="pc-incident-info-label" style={{ marginBottom: '6px' }}>
                        Triggered Rules
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                        {viewingIncident.matched_keywords.map((k, i) => (
                          <span key={i} className="pc-rule-tag">
                            {k}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                <div
                  style={{ marginTop: '14px', paddingTop: '12px', borderTop: '1px solid #f1f5f9' }}
                >
                  <div className="pc-incident-info-label" style={{ marginBottom: '6px' }}>
                    Sanitized Context Excerpt:
                  </div>
                  <pre className="pc-incident-snippet-pre">
                    {viewingIncident.sanitized_snippet || '[REDACTED CONTENT]'}
                  </pre>
                </div>
              </div>

              {/* Chronological Audit Trail Timeline */}
              {Array.isArray(viewingIncident.resolution_history) &&
                viewingIncident.resolution_history.length > 0 && (
                  <div className="pc-audit-section">
                    <div className="pc-incident-info-label" style={{ marginBottom: '10px' }}>
                      <i className="fas fa-history" style={{ marginRight: '6px' }}></i>
                      Resolution History &amp; Audit Trail (
                      {viewingIncident.resolution_history.length})
                    </div>
                    <div className="pc-audit-timeline">
                      {viewingIncident.resolution_history.map((item, idx) => {
                        const isReopened = item.action === 'reopened';
                        return (
                          <div key={idx} className="pc-timeline-row">
                            <div
                              className={`pc-timeline-dot ${isReopened ? 'reopened' : 'resolved'}`}
                            >
                              <i className={`fas ${isReopened ? 'fa-undo' : 'fa-check'}`}></i>
                            </div>
                            <div className="pc-timeline-body">
                              <div className="pc-timeline-heading">
                                <span
                                  className={`pc-badge-subtle ${isReopened ? 'flagged' : 'resolved'}`}
                                >
                                  {isReopened ? 'Reopened' : 'Resolved'}
                                </span>
                                <span className="pc-timeline-meta-text">
                                  by{' '}
                                  <strong>{item.resolved_by || item.reopened_by || 'Admin'}</strong>{' '}
                                  •{' '}
                                  {item.timestamp ? new Date(item.timestamp).toLocaleString() : ''}
                                </span>
                              </div>
                              {item.notes && item.notes.trim() && (
                                <div className="pc-timeline-note-text">{item.notes}</div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
            </div>

            <div className="pc-modal-footer" style={{ justifyContent: 'space-between' }}>
              <button
                type="button"
                className="pc-btn pc-btn-danger-outline"
                onClick={() => handleReopenIncident(viewingIncident.id)}
                disabled={actionLoading}
                title="Re-open this incident for further security investigation"
              >
                <i className="fas fa-undo"></i>
                <span>Re-open Incident</span>
              </button>
              <button
                type="button"
                className="pc-btn pc-btn-outline"
                onClick={() => setViewingIncident(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Custom In-App Confirmation Modal Dialog (replaces window.confirm) ── */}
      {confirmModal && (
        <div
          className="pc-confirm-overlay"
          onClick={closeConfirmModal}
          data-testid="pc-confirm-overlay"
        >
          <div
            className="pc-confirm-card"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="pc-confirm-title"
          >
            <div className="pc-confirm-top-bar">
              <div className={`pc-confirm-icon-box ${confirmModal.variant || 'warning'}`}>
                <i className={`fas ${confirmModal.icon || 'fa-exclamation-triangle'}`}></i>
              </div>
              <button
                type="button"
                className="pc-confirm-close-btn"
                onClick={closeConfirmModal}
                disabled={actionLoading}
                title="Cancel and close"
                aria-label="Close"
              >
                &times;
              </button>
            </div>

            <h4 id="pc-confirm-title" className="pc-confirm-title">
              {confirmModal.title}
            </h4>

            <p className="pc-confirm-message">{confirmModal.message}</p>

            {confirmModal.detail && (
              <div className={`pc-confirm-detail ${confirmModal.variant || 'warning'}`}>
                <i
                  className={`fas ${
                    confirmModal.variant === 'danger' ? 'fa-exclamation-circle' : 'fa-info-circle'
                  }`}
                  style={{ marginTop: '2px', flexShrink: 0 }}
                ></i>
                <span>{confirmModal.detail}</span>
              </div>
            )}

            <div className="pc-confirm-actions">
              <button
                type="button"
                className="pc-btn pc-btn-outline"
                onClick={closeConfirmModal}
                disabled={actionLoading}
              >
                {confirmModal.cancelText || 'Cancel'}
              </button>
              <button
                type="button"
                className={`pc-btn ${
                  confirmModal.variant === 'danger'
                    ? 'pc-btn-danger'
                    : confirmModal.variant === 'warning'
                      ? 'pc-btn-warning'
                      : 'pc-btn-primary'
                }`}
                onClick={handleDialogConfirm}
                disabled={actionLoading}
                autoFocus
              >
                {actionLoading ? (
                  <i className="fas fa-spinner fa-spin"></i>
                ) : (
                  <i className={`fas ${confirmModal.icon || 'fa-check'}`}></i>
                )}
                <span>{confirmModal.confirmText || 'Confirm'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
